import type { DateTime } from "luxon";
import { useMemo } from "react";
import type { Nurse, NurseInShift } from "../../../utils/models";
import { isAgencyNurse } from "../../../utils/warningDialogs/WarningDialog";
import { COLORS, matchesRoleGroup, ROLE_GROUPS } from "../types";
import { getMissingShiftSplit } from "./useHiringData";

export type DailyShiftCategory = "recurring" | "pickup" | "overtime" | "agency";

export const DAILY_DOT_ITEMS: { key: DailyShiftCategory; color: string }[] = [
	{ key: "recurring", color: COLORS.primary },
	{ key: "pickup", color: COLORS.success },
	{ key: "overtime", color: COLORS.warning },
	{ key: "agency", color: COLORS.error },
];

type DailyStatsByRoleDate = Record<string, Record<string, Record<DailyShiftCategory, number>>>;

interface UseDailyShiftBreakdownParams {
	dates: DateTime[];
	getShiftsForDate: (date: DateTime) => NurseInShift[];
	nurses: Nurse[];
}

export const useDailyShiftBreakdown = ({ dates, getShiftsForDate, nurses }: UseDailyShiftBreakdownParams): DailyStatsByRoleDate => {
	return useMemo(() => {
		const result: DailyStatsByRoleDate = {};

		for (const role of ROLE_GROUPS) {
			const perDateStats: Record<string, Record<DailyShiftCategory, number>> = {};

			for (const date of dates) {
				const dateISO = date.toISODate() || "";
				const shifts = getShiftsForDate(date).filter((s) => matchesRoleGroup(s.role, role));

				// Core non‑agency shifts for this role/date
				let core = 0;
				shifts.forEach((shift) => {
					const nurse = nurses.find((n) => n.id === shift.nurseId);
					const isAgency = !!(nurse?.agencyName || (nurse && isAgencyNurse(nurse.employmentType)));
					if (!isAgency) {
						core += 1;
					}
				});

				// Split core shifts into recurring / pickup / overtime / agency portions
				const split = getMissingShiftSplit(core);

				perDateStats[dateISO] = {
					recurring: core,
					pickup: split.pickup,
					overtime: split.overtime,
					agency: split.agency,
				};
			}

			result[role] = perDateStats;
		}

		return result;
	}, [dates, getShiftsForDate, nurses]);
};
